/* EAA-1821: the "Thời gian" facet's vocabulary and the preset -> concrete-date
   resolver, kept OUT of `AuditDateRangeChip.tsx`. Two reasons: react-refresh
   requires a module that exports a component to export nothing else, and the
   panel needs `resolveDateRange` to build its request without importing the
   chip's React tree at all. */

export type AuditDatePreset = 'today' | '7d' | '30d' | '90d' | 'all' | 'custom';

export interface AuditDateRangeValue {
    preset: AuditDatePreset;
    /** ISO `YYYY-MM-DD`; only meaningful while `preset === 'custom'`. */
    from?: string;
    to?: string;
}

/**
 * EAA-1821: the translated copy every date surface takes. Named (rather than
 * re-inlined per component) because the SAME object now travels to three
 * places — the bar's `AuditDateRangeChip`, the shared `AuditDatePresetList`
 * and the table header's date funnel — and while the shape was written out
 * three times the funnel simply never got one, so it fell back to the
 * hardcoded Vietnamese below even with the console in English.
 */
export interface AuditDatePresetLabels {
    presets?: Partial<Record<AuditDatePreset, string>>;
    from?: string;
    to?: string;
}

/** Default preset copy. Override per-key through the chip's `labels.presets`. */
export const AUDIT_DATE_PRESETS: Record<AuditDatePreset, string> = {
    today: 'Hôm nay',
    '7d': '7 ngày',
    '30d': '30 ngày',
    '90d': '90 ngày',
    all: 'Tất cả',
    custom: 'Tuỳ chọn…',
};

/** Render order — `all` sits before `custom` so the escape hatch is last. */
export const AUDIT_DATE_PRESET_ORDER: AuditDatePreset[] = [
    'today',
    '7d',
    '30d',
    '90d',
    'all',
    'custom',
];

/** How many days back each rolling window reaches, INCLUSIVE of today. */
const PRESET_SPAN_DAYS: Partial<Record<AuditDatePreset, number>> = {
    today: 1,
    '7d': 7,
    '30d': 30,
    '90d': 90,
};

// EAA-1821: format from the LOCAL date parts, not `toISOString()`. The latter
// converts to UTC first, so anywhere east of Greenwich "today" silently becomes
// yesterday's date for the first hours of the day — the exact class of off-by-one
// that makes an audit filter look broken to a Vietnam-based operator (UTC+7).
function toISODate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * EAA-1821: turn a preset into the concrete `YYYY-MM-DD` bounds the audit API
 * takes. Lives beside the chip's vocabulary (rather than in the panel) so the
 * presets and their meaning cannot drift apart — a new preset is one entry in
 * `PRESET_SPAN_DAYS`, one in the two maps above, and nothing else.
 *
 * `all` yields an empty range (no bound sent at all), `custom` passes the
 * operator's own values straight through.
 */
export function resolveDateRange(value: AuditDateRangeValue): { from?: string; to?: string } {
    if (value.preset === 'all') return {};
    if (value.preset === 'custom') return { from: value.from, to: value.to };

    const span = PRESET_SPAN_DAYS[value.preset];
    if (!span) return {};

    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (span - 1));
    return { from: toISODate(from), to: toISODate(to) };
}
